import { Message } from '../models/Message';
import {
  getSessionAuthorizationHeaders,
} from './sessionAuth';

export type ConversationMode =
  | 'conversation'
  | 'grammar'
  | 'task_review';

interface SessionResponse {
  ok: boolean;
  authenticated?: boolean;
  csrf_token?: string;
}

interface ConversationResponse {
  ok: boolean;
  error?: string;
  conversation?: {
    id: number;
    inactive_24h?: boolean;
    last_message_at?: string | null;
  } | null;
  messages?: Message[];
  api_context?: Message[];
}

export interface RestoredConversation {
  id: number;
  inactive24h: boolean;
  messages: Message[];
  apiContext: Message[];
}

let csrfTokenPromise: Promise<string> | null = null;

const getCsrfToken = async (): Promise<string> => {
  if (!csrfTokenPromise) {
    csrfTokenPromise = fetch('/api/session.php', {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: {
        ...getSessionAuthorizationHeaders(),
        Accept: 'application/json',
      },
    })
      .then(async response => {
        const data =
          (await response.json()) as SessionResponse;

        if (
          !response.ok ||
          !data.ok ||
          !data.authenticated ||
          !data.csrf_token
        ) {
          throw new Error(
            'No fue posible obtener el token de seguridad.'
          );
        }

        return data.csrf_token;
      })
      .catch(error => {
        csrfTokenPromise = null;
        throw error;
      });
  }

  return csrfTokenPromise;
};

export const loadConversation = async (
  language: string,
  mode: ConversationMode
): Promise<RestoredConversation | null> => {
  const parameters = new URLSearchParams({
    language,
    mode,
  });

  const response = await fetch(
    `/api/conversation.php?${parameters.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: {
        ...getSessionAuthorizationHeaders(),
        Accept: 'application/json',
      },
    }
  );

  const data =
    (await response.json()) as ConversationResponse;

  if (!response.ok || !data.ok) {
    throw new Error(
      data.error ?? 'No fue posible recuperar la conversación.'
    );
  }

  if (
    !data.conversation ||
    !Array.isArray(data.messages)
  ) {
    return null;
  }

  return {
    id: data.conversation.id,
    inactive24h: data.conversation.inactive_24h === true,
    messages: data.messages,
    apiContext: Array.isArray(data.api_context)
      ? data.api_context
      : [],
  };
};

export const saveConversation = async (
  language: string,
  mode: ConversationMode,
  messages: Message[],
  apiContext: Message[],
  conversationId?: number
): Promise<void> => {
  const csrfToken = await getCsrfToken();

  const response = await fetch('/api/conversation.php', {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: {
      ...getSessionAuthorizationHeaders(),
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({
      language,
      mode,
      conversation_id: conversationId,
      messages,
      // Persistence accepts conversation turns only; system instructions are
      // restored from workspaceConfig when opening the conversation.
      api_context: apiContext
        .filter(message => message.role === 'user' || message.role === 'assistant')
        .slice(-40),
    }),
  });

  const data =
    (await response.json()) as ConversationResponse;

  if (!response.ok || !data.ok) {
    throw new Error(
      data.error ?? 'No fue posible guardar la conversación.'
    );
  }
};

export const changeConversation = async (
  language: string, mode: ConversationMode, conversationId: number, action: 'ensure' | 'new'
): Promise<void> => {
  const csrfToken = await getCsrfToken();
  const response = await fetch('/api/conversation.php', {
    method:'POST', credentials:'include', cache:'no-store',
    headers:{...getSessionAuthorizationHeaders(), 'Content-Type':'application/json', 'X-CSRF-Token':csrfToken},
    body:JSON.stringify({language,mode,conversation_id:conversationId,action}),
  });
  const data = await response.json();
  if (!response.ok || !data.ok) throw new Error(data.error ?? 'No se pudo iniciar la conversación.');
};
